import {z} from 'zod';
import {
	editEffectsSortOrder,
	type EditEffectsSortOrderParams,
	getSortField,
	type GetSortFieldParams,
	getSortOrder,
	type GetSortOrderParams,
	SortOrder,
	SortSchema,
} from '@netx/core-schema';
import {
	AssetProjectReviewField,
	type IAssetProjectReview,
} from './assetProjectReviewSchema';

export const ASSET_PROJECT_REVIEW_SORTABLE_FIELDS = [
	AssetProjectReviewField.id,
	AssetProjectReviewField.title,
] as const;

export const AssetProjectReviewSortSchema = SortSchema.omit({
	field: true,
}).extend({
	field: z.enum(ASSET_PROJECT_REVIEW_SORTABLE_FIELDS),
});

export type AssetProjectReviewSort = z.infer<
	typeof AssetProjectReviewSortSchema
>;

export const AssetProjectReviewSortableField = z.enum(
	ASSET_PROJECT_REVIEW_SORTABLE_FIELDS,
).enum;

export const ASSET_PROJECT_REVIEW_DEFAULT_SORT_FIELD =
	AssetProjectReviewSortableField.id;

export const ASSET_PROJECT_REVIEW_DEFAULT_SORT_ORDER = SortOrder.ASCENDING;

/** will return the sort passed in if valid/specified, otherwise will return the defaults for the invalid/unspecified */
export const getAssetProjectReviewDefaultSort = (
	sort: Partial<AssetProjectReviewSort> = {},
): AssetProjectReviewSort => ({
	field: getAssetProjectReviewSortField({sortField: sort.field}),
	order: getAssetProjectReviewSortOrder({sortOrder: sort.order}),
});

/** will return the passed in sort field if valid, otherwise will retutn the default sort field */
export const getAssetProjectReviewSortField = (
	params: Omit<
		GetSortFieldParams<(typeof ASSET_PROJECT_REVIEW_SORTABLE_FIELDS)[number]>,
		'defaultSortField' | 'sortableFields'
	>,
) =>
	getSortField({
		...params,
		sortableFields: ASSET_PROJECT_REVIEW_SORTABLE_FIELDS,
		defaultSortField: ASSET_PROJECT_REVIEW_DEFAULT_SORT_FIELD,
	});

/** will return the passed in sort order if valid, otherwise will return the default sort order */
export const getAssetProjectReviewSortOrder = (
	params: Omit<GetSortOrderParams, 'defaultSortOrder'>,
) =>
	getSortOrder({
		...params,
		defaultSortOrder: ASSET_PROJECT_REVIEW_DEFAULT_SORT_ORDER,
	});

/** given the original data, the new data, and the current sort, determine if the sort order was effected */
export const assetProjectReviewEditEffectsOrder = (
	params: Omit<
		EditEffectsSortOrderParams<
			IAssetProjectReview,
			(typeof ASSET_PROJECT_REVIEW_SORTABLE_FIELDS)[number]
		>,
		'sortableFields'
	>,
) =>
	editEffectsSortOrder({
		...params,
		sortableFields: ASSET_PROJECT_REVIEW_SORTABLE_FIELDS,
	});
